import { Injectable } from '@angular/core';
import { ApiService, Pagination, PER_PAGE } from './api.service';
import { ActivatedRoute } from '@angular/router';
import { BehaviorSubject, Observable, shareReplay, Subject, switchMap, tap, take } from 'rxjs';
import { Paginate } from '@shared/entities/paginate';
import { SalesOrderDocuments } from '@shared/entities/salesOrderDocuments';

const LOG_PREFIX = '[Api][SalesOrderDocuments] ';

export interface SalesOrderDocumentsFilterURL {
  keyword?: string;
  id?: string;
  salesOrderId?: string;
  requiredDocumentId?: string;
  name?: string;
  serialNumber?: string;
  displayOrder?: number;
  customerCanUpload?: boolean;
  uploadablePeriodically?: boolean;
  associatedToMachine?: boolean;
  notes?: string;
  templateId?: string;
  orderBy?: string;
  direction?: 'ASC' | 'DESC';
  locationId?: string;
}

@Injectable({
  providedIn: 'root'
})
export class SalesOrderDocumentsService extends ApiService<SalesOrderDocuments> {
  private _refresh$: Subject<Pagination>;
  allSalesOrderDocuments$: Observable<Paginate<SalesOrderDocuments>>;
  private _currentPaginator: Pagination;
  private _currentFilters: SalesOrderDocumentsFilterURL = {};

  set filters(value: SalesOrderDocumentsFilterURL) {
    this._currentFilters = value;
    this.refresh(1, PER_PAGE);
  }

  constructor(private _activatedRoute: ActivatedRoute) {
    const masterUrl = '/sales-order-documents';
    const detailUrl = '/sales-order-documents';

    super(SalesOrderDocuments, masterUrl, detailUrl);

    this._currentPaginator = { page: 1, perPage: PER_PAGE };

    this._refresh$ = new BehaviorSubject<Pagination>(this._currentPaginator);
    this.allSalesOrderDocuments$ = this._refresh$.pipe(
      switchMap((pagination) => {
        const { locationId } = this._activatedRoute.snapshot.queryParams;
        if (locationId) {
          this.setUrlParameter('locationId', locationId);
        }

        if (this._currentFilters.keyword !== undefined) {
          this.setUrlParameter('keyword', this._currentFilters.keyword);
        }
        if (this._currentFilters.id !== undefined) {
          this.setUrlParameter('id', this._currentFilters.id);
        } else {
          this.removeUrlParameter('id');
        }
        if (this._currentFilters.salesOrderId !== undefined) {
          this.setUrlParameter('salesOrderId', this._currentFilters.salesOrderId);
        }
        if (this._currentFilters.requiredDocumentId !== undefined) {
          this.setUrlParameter('requiredDocumentId', this._currentFilters.requiredDocumentId);
        }
        if (this._currentFilters.name !== undefined) {
          this.setUrlParameter('name', this._currentFilters.name);
        }
        if (this._currentFilters.serialNumber !== undefined) {
          this.setUrlParameter('serialNumber', this._currentFilters.serialNumber);
        }
        if (this._currentFilters.displayOrder !== undefined) {
          this.setUrlParameter('displayOrder', this._currentFilters.displayOrder.toString());
        }
        if (this._currentFilters.customerCanUpload !== undefined) {
          this.setUrlParameter('customerCanUpload', this._currentFilters.customerCanUpload.toString());
        }
        if (this._currentFilters.uploadablePeriodically !== undefined) {
          this.setUrlParameter('uploadablePeriodically', this._currentFilters.uploadablePeriodically.toString());
        }
        if (this._currentFilters.associatedToMachine !== undefined) {
          this.setUrlParameter('associatedToMachine', this._currentFilters.associatedToMachine.toString());
        }
        if (this._currentFilters.notes !== undefined) {
          this.setUrlParameter('notes', this._currentFilters.notes);
        }
        if (this._currentFilters.templateId !== undefined) {
          this.setUrlParameter('templateId', this._currentFilters.templateId);
        }

        const orderBy = this._currentFilters.orderBy || 'displayOrder';
        this.setUrlParameter('order_by', orderBy);

        if (orderBy === 'sodName') {
          this.setUrlParameter('direction', 'ASC');
        } else if (orderBy === 'displayOrder') {
          this.setUrlParameter('direction', 'ASC');
        } else {
          this.setUrlParameter('direction', 'DESC');
        }

        return this.paginate(SalesOrderDocuments, pagination.page, pagination.perPage);
      }),
      tap({
        next: (result) => this.logger.debug(LOG_PREFIX + 'all$ sales order documents', result),
        error: (err) => this.logger.error(LOG_PREFIX + 'all$ sales order documents error', err)
      }),
      shareReplay(1)
    );
  }

  refresh(page: number, perPage: number = PER_PAGE) {
    this._currentPaginator = { page, perPage };
    this._refresh$.next(this._currentPaginator);
    return this.allSalesOrderDocuments$.pipe(take(1));
  }

  refreshFilters() {
    this._currentFilters = {};
    this.refresh(1, PER_PAGE);
  }

  getList(
    params: {
      page?: number;
      size?: number;
      orderBy?: string;
      direction?: 'ASC' | 'DESC';
      [key: string]: any;
    } = {}
  ): Observable<Paginate<SalesOrderDocuments>> {
    Array.from(this.getUrlParameters().keys()).forEach((key) => this.removeUrlParameter(key));
    if (params.page) this.setUrlParameter('page', params.page.toString());
    if (params.size) this.setUrlParameter('size', params.size.toString());
    if (params.orderBy) this.setUrlParameter('orderBy', params.orderBy);
    if (params.direction) this.setUrlParameter('direction', params.direction);

    Object.keys(params).forEach((key) => {
      if (
        !['page', 'size', 'orderBy', 'direction'].includes(key) &&
        params[key] !== undefined &&
        params[key] !== null
      ) {
        this.setUrlParameter(key, params[key]);
      }
    });
    return this.paginate(SalesOrderDocuments, params.page || 1, params.size || PER_PAGE);
  }

  getBySalesOrderId(salesOrderId: string): Observable<Paginate<SalesOrderDocuments>> {
    return this.getList({ salesOrderId });
  }

  getByRequiredDocumentId(requiredDocumentId: string): Observable<Paginate<SalesOrderDocuments>> {
    return this.getList({ requiredDocumentId });
  }

  deleteSalesOrderDocument(id: string): Observable<boolean> {
    this.setUrlParameter('id', id);
    return this.remove(id);
  }

  updateHiddenForCustomerPartner(id: string, hidden: boolean): Observable<SalesOrderDocuments> {
    const payload = Object.assign(new SalesOrderDocuments(), { id, hiddenForCustomerPartner: hidden });
    return this.save(payload, id);
  }
}
